import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import type { Editor } from '@tiptap/react'
import { isPreviewMode } from '@/lib/utils'

interface EditContextValue {
  isEditMode: boolean
  isPreviewMode: boolean
  dirtySections: Set<string>
  activeEditor: Editor | null
  setActiveEditor: (editor: Editor | null) => void
  markDirty: (key: string) => void
  markClean: (key: string) => void
  hasUnsavedChanges: boolean
  publishCallbacks: Map<string, () => Promise<void>>
  registerPublishCallback: (key: string, callback: () => Promise<void>) => void
  unregisterPublishCallback: (key: string) => void
  publishAll: () => Promise<void>
  discardAll: () => void
  discardCallbacks: Map<string, () => void>
  registerDiscardCallback: (key: string, callback: () => void) => void
  unregisterDiscardCallback: (key: string) => void
}

const EditContext = createContext<EditContextValue | null>(null)

export function useEditContext() {
  const context = useContext(EditContext)
  if (!context) {
    // Return a default context for non-edit mode
    return {
      isEditMode: false,
      isPreviewMode: isPreviewMode(),
      dirtySections: new Set<string>(),
      activeEditor: null,
      setActiveEditor: () => {},
      markDirty: () => {},
      markClean: () => {},
      hasUnsavedChanges: false,
      publishCallbacks: new Map(),
      registerPublishCallback: () => {},
      unregisterPublishCallback: () => {},
      publishAll: async () => {},
      discardAll: () => {},
      discardCallbacks: new Map(),
      registerDiscardCallback: () => {},
      unregisterDiscardCallback: () => {},
    }
  }
  return context
}

interface EditProviderProps {
  children: ReactNode
}

export function EditProvider({ children }: EditProviderProps) {
  const location = useLocation()
  const isEditMode = location.pathname.startsWith('/admin')
  const previewMode = isPreviewMode()

  const [dirtySections, setDirtySections] = useState<Set<string>>(new Set())
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null)
  const [publishCallbacks] = useState<Map<string, () => Promise<void>>>(new Map())
  const [discardCallbacks] = useState<Map<string, () => void>>(new Map())

  const markDirty = useCallback((key: string) => {
    setDirtySections((prev) => {
      const next = new Set(prev)
      next.add(key)
      return next
    })
  }, [])

  const markClean = useCallback((key: string) => {
    setDirtySections((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }, [])

  const hasUnsavedChanges = dirtySections.size > 0

  const registerPublishCallback = useCallback(
    (key: string, callback: () => Promise<void>) => {
      publishCallbacks.set(key, callback)
    },
    [publishCallbacks]
  )

  const unregisterPublishCallback = useCallback(
    (key: string) => {
      publishCallbacks.delete(key)
    },
    [publishCallbacks]
  )

  const registerDiscardCallback = useCallback(
    (key: string, callback: () => void) => {
      discardCallbacks.set(key, callback)
    },
    [discardCallbacks]
  )

  const unregisterDiscardCallback = useCallback(
    (key: string) => {
      discardCallbacks.delete(key)
    },
    [discardCallbacks]
  )

  const publishAll = useCallback(async () => {
    const dirtyKeys = Array.from(dirtySections)
    await Promise.all(
      dirtyKeys.map((key) => {
        const callback = publishCallbacks.get(key)
        return callback ? callback() : Promise.resolve()
      })
    )
  }, [dirtySections, publishCallbacks])

  const discardAll = useCallback(() => {
    const dirtyKeys = Array.from(dirtySections)
    dirtyKeys.forEach((key) => {
      const callback = discardCallbacks.get(key)
      if (callback) callback()
    })
  }, [dirtySections, discardCallbacks])

  const value = useMemo(
    () => ({
      isEditMode,
      isPreviewMode: previewMode,
      dirtySections,
      activeEditor,
      setActiveEditor,
      markDirty,
      markClean,
      hasUnsavedChanges,
      publishCallbacks,
      registerPublishCallback,
      unregisterPublishCallback,
      publishAll,
      discardAll,
      discardCallbacks,
      registerDiscardCallback,
      unregisterDiscardCallback,
    }),
    [
      isEditMode,
      previewMode,
      dirtySections,
      activeEditor,
      markDirty,
      markClean,
      hasUnsavedChanges,
      publishCallbacks,
      registerPublishCallback,
      unregisterPublishCallback,
      publishAll,
      discardAll,
      discardCallbacks,
      registerDiscardCallback,
      unregisterDiscardCallback,
    ]
  )

  return <EditContext.Provider value={value}>{children}</EditContext.Provider>
}
